/**
 * California statewide GHG inventory chart = sum of CA county rows in ghg_inventory_precalculated
 * (geography_type County, geofips 6000–6999). Matches upsert_california_state_ghg_from_counties basis.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import type { GHGInventoryResult } from '../components/charts/ghgInventoryChartTypes';
import { computeSectorInventoryHeadlines } from '../utils/ghgInventoryHeadlineMetrics';
import { ghgInventoryCategoryRgba } from '../theme/navChartPalette';

type PrecalcCountyRow = {
  geofips: number;
  year: number;
  calculation_date?: string | null;
  ldv_emissions: number | null;
  hdv_emissions: number | null;
  elec_residential_emissions: number | null;
  elec_commercial_emissions: number | null;
  ng_residential_emissions: number | null;
  ng_commercial_emissions: number | null;
  waste_emissions: number | null;
  offroad_emissions: number | null;
  agriculture_emissions: number | null;
};

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function dedupeCountyRowsByGeofipsYear(rows: PrecalcCountyRow[]): PrecalcCountyRow[] {
  const indexed = rows.map((r, i) => ({ r, i }));
  indexed.sort((a, b) => {
    const ta = a.r.calculation_date ? new Date(a.r.calculation_date).getTime() : 0;
    const tb = b.r.calculation_date ? new Date(b.r.calculation_date).getTime() : 0;
    if (ta !== tb) return ta - tb;
    return a.i - b.i;
  });
  const m = new Map<string, PrecalcCountyRow>();
  for (const { r } of indexed) {
    const g = Number(r.geofips);
    const y = Number(r.year);
    if (!Number.isFinite(g) || !Number.isFinite(y)) continue;
    m.set(`${g}|${y}`, r);
  }
  return Array.from(m.values());
}

async function fetchAllCaliforniaCountyPrecalcRows(client: SupabaseClient): Promise<PrecalcCountyRow[]> {
  const rows: PrecalcCountyRow[] = [];
  const pageSize = 1000;
  let from = 0;

  for (;;) {
    const { data, error } = await client
      .from('ghg_inventory_precalculated')
      .select(
        'geofips,year,calculation_date,ldv_emissions,hdv_emissions,elec_residential_emissions,elec_commercial_emissions,ng_residential_emissions,ng_commercial_emissions,waste_emissions,offroad_emissions,agriculture_emissions'
      )
      .eq('geography_type', 'County')
      .gte('geofips', 6000)
      .lt('geofips', 7000)
      .order('year', { ascending: true })
      .range(from, from + pageSize - 1);

    if (error) {
      console.warn('fetchAllCaliforniaCountyPrecalcRows:', error.message);
      return [];
    }

    const batch = (data ?? []) as PrecalcCountyRow[];
    if (batch.length === 0) break;
    rows.push(...batch);
    if (batch.length < pageSize) break;
    from += pageSize;
  }

  return rows;
}

/** Same sector labels / stack order as GHGInventorySupabase allEmissions assembly. */
const CATEGORY_ORDER = [
  'Light Duty Vehicles',
  'Heavy Duty Vehicles',
  'Natural Gas Residential',
  'Electricity Commercial',
  'Electricity Residential',
  'Natural Gas Commercial',
  'Waste',
  'Offroad',
  'Agriculture',
] as const;

function emissionsForCategory(
  sums: {
    ldv: number;
    hdv: number;
    ngRes: number;
    ngCom: number;
    elecRes: number;
    elecCom: number;
    waste: number;
    offroad: number;
    ag: number;
  },
  category: (typeof CATEGORY_ORDER)[number]
): number {
  switch (category) {
    case 'Light Duty Vehicles':
      return Math.round(sums.ldv);
    case 'Heavy Duty Vehicles':
      return Math.round(sums.hdv);
    case 'Natural Gas Residential':
      return Math.round(sums.ngRes);
    case 'Electricity Commercial':
      return Math.round(sums.elecCom);
    case 'Electricity Residential':
      return Math.round(sums.elecRes);
    case 'Natural Gas Commercial':
      return Math.round(sums.ngCom);
    case 'Waste':
      return Math.round(sums.waste);
    case 'Offroad':
      return Math.round(sums.offroad);
    case 'Agriculture':
      return Math.round(sums.ag);
    default:
      return 0;
  }
}

function getColorForCategory(cat: string): { bg: string; border: string } {
  switch (cat) {
    case 'Light Duty Vehicles':
      return { bg: 'rgba(59, 130, 246, 0.8)', border: 'rgba(59, 130, 246, 1)' };
    case 'Heavy Duty Vehicles':
      return { bg: 'rgba(96, 165, 250, 0.8)', border: 'rgba(96, 165, 250, 1)' };
    case 'Natural Gas Residential':
    case 'Natural Gas Commercial':
    case 'Electricity Residential':
    case 'Electricity Commercial':
      return ghgInventoryCategoryRgba(cat);
    case 'Waste':
      return { bg: 'rgba(236, 72, 153, 0.8)', border: 'rgba(236, 72, 153, 1)' };
    case 'Offroad':
      return { bg: 'rgba(255, 210, 0, 0.8)', border: 'rgba(255, 210, 0, 1)' };
    case 'Agriculture':
      return { bg: 'rgba(22, 101, 52, 0.85)', border: 'rgba(22, 101, 52, 1)' };
    default:
      return { bg: 'rgba(100, 116, 139, 0.8)', border: 'rgba(100, 116, 139, 1)' };
  }
}

/**
 * Build GHGInventoryResult by summing precalculated county inventory rows (CA counties only).
 */
export async function fetchCaliforniaGhgInventoryResultFromCountyPrecalc(
  client: SupabaseClient,
  location: string
): Promise<GHGInventoryResult> {
  const raw = await fetchAllCaliforniaCountyPrecalcRows(client);
  const deduped = dedupeCountyRowsByGeofipsYear(raw);

  const byYear = new Map<
    number,
    {
      ldv: number;
      hdv: number;
      ngRes: number;
      ngCom: number;
      elecRes: number;
      elecCom: number;
      waste: number;
      offroad: number;
      ag: number;
    }
  >();

  for (const r of deduped) {
    const y = Number(r.year);
    if (!Number.isFinite(y)) continue;
    let s = byYear.get(y);
    if (!s) {
      s = { ldv: 0, hdv: 0, ngRes: 0, ngCom: 0, elecRes: 0, elecCom: 0, waste: 0, offroad: 0, ag: 0 };
      byYear.set(y, s);
    }
    s.ldv += num(r.ldv_emissions);
    s.hdv += num(r.hdv_emissions);
    s.ngRes += num(r.ng_residential_emissions);
    s.ngCom += num(r.ng_commercial_emissions);
    s.elecRes += num(r.elec_residential_emissions);
    s.elecCom += num(r.elec_commercial_emissions);
    s.waste += num(r.waste_emissions);
    s.offroad += num(r.offroad_emissions);
    s.ag += num(r.agriculture_emissions);
  }

  const years = Array.from(byYear.keys()).sort((a, b) => a - b);
  if (years.length === 0) {
    console.warn(
      'GHG Inventory (California): no county rows in ghg_inventory_precalculated (6000–6999). Run populate for counties, then upsert state from counties.'
    );
  }

  const allEmissions: { year: number; category: string; emissions: number }[] = [];
  for (const year of years) {
    const sums = byYear.get(year)!;
    for (const category of CATEGORY_ORDER) {
      allEmissions.push({
        year,
        category,
        emissions: emissionsForCategory(sums, category),
      });
    }
  }

  const datasets = CATEGORY_ORDER.map((category) => {
    const colors = getColorForCategory(category);
    const data = years.map((year) => {
      const sums = byYear.get(year);
      if (!sums) return 0;
      return emissionsForCategory(sums, category);
    });
    return {
      label: category,
      data,
      backgroundColor: colors.bg,
      borderColor: colors.border,
      borderWidth: 2,
    };
  });

  const chartData = { labels: years, datasets };
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
        },
      },
      title: {
        display: true,
        text: `GHG Inventory - ${location} (sum of county precalculated rows)`,
        font: { size: 16, weight: 'bold' as const },
      },
      tooltip: {
        enabled: true,
        mode: 'index' as const,
        intersect: false,
        callbacks: {
          label: (context: { dataset: { label?: string }; parsed: { y: number } }) => {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            return `${label}: ${value.toLocaleString()} MT CO₂e`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Emissions (Metric Tons CO₂e)',
        },
        ticks: {
          callback: (value: string | number) => Number(value).toLocaleString(),
        },
      },
      x: {
        title: {
          display: true,
          text: 'Year',
        },
      },
    },
  };

  const latestYear = years.length ? years[years.length - 1] : undefined;
  const latestYearData = latestYear != null ? allEmissions.filter((d) => d.year === latestYear) : [];
  const totalEmissions = latestYearData.reduce((sum, d) => sum + d.emissions, 0);
  const lightDutyLatest =
    latestYearData.find((d) => d.category === 'Light Duty Vehicles')?.emissions || 0;
  const heavyDutyLatest =
    latestYearData.find((d) => d.category === 'Heavy Duty Vehicles')?.emissions || 0;
  const naturalGasLatest =
    latestYearData.find((d) => d.category === 'Natural Gas Residential')?.emissions || 0;

  const headlines = computeSectorInventoryHeadlines(allEmissions);

  return {
    chartData,
    chartOptions,
    latestYear,
    totalEmissions,
    lightDutyEmissions: lightDutyLatest,
    heavyDutyEmissions: heavyDutyLatest,
    naturalGasEmissions: naturalGasLatest,
    totalEmissions2005: headlines.totalEmissions2005,
    totalEmissions2023: headlines.totalEmissions2023,
    percentChange: headlines.percentChange2023Vs2005,
    largestSectorSharePercent2023: headlines.largestSectorSharePercent2023,
    largestSectorName2023: headlines.largestSectorName2023,
  };
}
